/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Actions, ofType } from '@ngrx/effects';
import { exhaustMap, expand, filter, Observable, of, Subject, takeUntil, tap, timer } from 'rxjs';
import { InjectorService } from './injector/injector.service';
import { serverErrorAction } from './ngrx/actions';
import { StateResource } from './resource/resource.util';

export const DEFAULT_INTERVALL_TIMER: number = 500;

export interface IntervallHandleWithTickObservable {
  handle: number;
  tickObservable: Observable<any>;
}

export function pollWhile<T>(
  initial: T,
  pollFunction: (current: T) => Observable<T>,
  whileFn: (current: T) => boolean,
  delayTime: number = DEFAULT_INTERVALL_TIMER,
): Observable<T> {
  const actions$: Actions = InjectorService.getInjector().get(Actions);
  return of(initial).pipe(
    expand((current: T) =>
      timer(delayTime).pipe(
        filter(() => whileFn(current)),
        exhaustMap(() => pollFunction(current)),
      ),
    ),
    takeUntil(actions$.pipe(ofType(serverErrorAction))),
  );
}

export function doInIntervall<T>(
  pollObj$: Observable<StateResource<T>>,
  untilFn: (stateResource: StateResource<T>) => boolean,
  intervallTime: number = DEFAULT_INTERVALL_TIMER,
): Observable<StateResource<T>> {
  const interval: IntervallHandleWithTickObservable = startInterval(intervallTime);
  const actions$: Actions = InjectorService.getInjector().get(Actions);
  return interval.tickObservable.pipe(
    exhaustMap(() => pollObj$),
    tap((state) => {
      if (untilFn(state)) Interval.clearInterval(interval.handle);
    }),
    takeUntil(actions$.pipe(ofType(serverErrorAction))),
  );
}

export function startInterval(interval: number): IntervallHandleWithTickObservable {
  const subj: Subject<any> = new Subject();
  // Workaround: cast return value of setInterval() to number because since TS 4.8 it thinks it must be "Timer"
  const handle = setInterval(() => subj.next('tick'), interval) as unknown as number;

  return { handle, tickObservable: subj.asObservable() };
}

export function clearInterval(handler: number): void {
  window.clearInterval(handler);
}

export const Interval = {
  doInIntervall,
  startInterval,
  clearInterval,
};
